import "@/App.css";
// Preview frontend refresh marker: force Pages rebuild after Open Library admin cleanup.
import { BrowserRouter, Routes, Route, useParams, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { LocalizationProvider } from "@/localization/LocalizationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HallReturnRouteMemory from "@/components/HallReturnRouteMemory";
import AdminRoute from "@/components/AdminRoute";
import EditorialRoute from "@/components/EditorialRoute";
import OwnerRoute from "@/components/OwnerRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MainHall from "@/pages/MainHall";
import WelcomeDoor from "@/pages/WelcomeDoor";
import Architect from "@/pages/Architect";
import ArchitectGate from "@/pages/ArchitectGate";
import MyConstructions from "@/pages/MyConstructions";
import Account from "@/pages/Account";
import MyMaterialDetail from "@/pages/MyMaterialDetail";
import Admin from "@/pages/Admin";
import AdminMaterialPreview from "@/pages/AdminMaterialPreview";
import Library from "@/pages/Library";
import MaterialDetail from "@/pages/MaterialDetail";
import EditorialPublicMaterial from "@/pages/EditorialPublicMaterial";
import ProductView from "@/pages/ProductView";
import ProtectedMaterialDetail from "@/pages/ProtectedMaterialDetail";
import Placeholder from "@/pages/Placeholder";
import PublicOrderView from "@/pages/PublicOrderView";
import MOShell, { MOAccessProvider } from "@/components/masterOrder/MOShell";
import MasterOrderOverview from "@/pages/MasterOrderOverview";
import MasterOrderTesting from "@/pages/MasterOrderTesting";
import MasterOrderTestingFeedback from "@/pages/MasterOrderTestingFeedback";
import MOOrders from "@/pages/masterOrder/MOOrders";
import MOOrderNew from "@/pages/masterOrder/MOOrderNew";
import MOOrderDetail from "@/pages/masterOrder/MOOrderDetail";
import MOClients from "@/pages/masterOrder/MOClients";
import MOClientDetail from "@/pages/masterOrder/MOClientDetail";
import { MOCostings, MOCostingNew, MOCostingStudio } from "@/pages/masterOrder/MOCosting";
import MOIngredients from "@/pages/masterOrder/MOIngredients";
import MORecipes from "@/pages/masterOrder/MORecipes";
import MOPackaging from "@/pages/masterOrder/MOPackaging";
import MOPurchases from "@/pages/masterOrder/MOPurchases";
import MOProduction from "@/pages/masterOrder/MOProduction";
import MOProductionScenarios from "@/pages/masterOrder/MOProductionScenarios";
import MORecipeRecalc from "@/pages/masterOrder/MORecipeRecalc";
import MOMeasureDirectory from "@/pages/masterOrder/MOMeasureDirectory";
import MOCalendar from "@/pages/masterOrder/MOCalendar";
import MOProductCost from "@/pages/masterOrder/MOProductCost";
import MOBrandStudio from "@/pages/masterOrder/MOBrandStudio";
import MOBrandStudioHomeV2 from "@/pages/masterOrder/MOBrandStudioHomeV2";
import BrandStudioCreateV2 from "@/pages/masterOrder/BrandStudioCreateV2";
import BrandStudioSpaceV2 from "@/pages/masterOrder/BrandStudioSpaceV2";
import MOBrandReferences from "@/pages/masterOrder/MOBrandReferences";
import MONewVisualDirection from "@/pages/masterOrder/MONewVisualDirection";
import MOVisualDirection from "@/pages/masterOrder/MOVisualDirection";
import MOVisualDNA from "@/pages/masterOrder/MOVisualDNA";
import MOLogoWorkshop from "@/pages/masterOrder/MOLogoWorkshop";
import MOBrandStudioWorkspace from "@/pages/masterOrder/MOBrandStudioWorkspace";
import BrandStudioAccessProvider from "@/components/brandStudio/BrandStudioAccessProvider";
import ProductAccessGate from "@/components/ProductAccessGate";
import { BrandStudioLegacyRedirect, BrandStudioLegacyWorkspaceRedirect } from "@/components/brandStudio/BrandStudioLegacyRedirect";
import ProgramsLanding from "@/pages/ProgramsLanding";
import ProgramDetail from "@/pages/ProgramDetail";
import AlmondLanding from "@/pages/AlmondLanding";
import GingerbreadLanding from "@/pages/GingerbreadLanding";
import ProfessionalMapLanding from "@/pages/ProfessionalMapLanding";
import ProfessionalMapSection from "@/pages/ProfessionalMapSection";
import ProfessionalMapDiscovery from "@/pages/ProfessionalMapDiscovery";
import ProfessionalCardWorkspace from "@/pages/ProfessionalCardWorkspace";
import ProfessionalMapOwnerQa from "@/pages/ProfessionalMapOwnerQa";
import ProfessionalMapPublicProfile from "@/pages/ProfessionalMapPublicProfile";
import ProfessionalMapReviewsStaff from "@/pages/ProfessionalMapReviewsStaff";
import ProfessionalQuestionsPage, { ProfessionalQuestionDetail } from "@/pages/ProfessionalQuestions";
import ProfessionalQuestionsStaff from "@/pages/ProfessionalQuestionsStaff";
import LegalCenter from "@/pages/LegalCenter";
import LegalDataProcessing from "@/pages/LegalDataProcessing";
import LegalPublicationConsent from "@/pages/LegalPublicationConsent";
import LegalOffer from "@/pages/LegalOffer";
import LegalMarketingConsent from "@/pages/LegalMarketingConsent";
import LegalRuPersonalDataConsent from "@/pages/LegalRuPersonalDataConsent";
import LegalPrivacy from "@/pages/LegalPrivacy";
import CheckoutStart from "@/pages/CheckoutStart";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentFailed from "@/pages/PaymentFailed";
import VoiceFoundationProof from "@/pages/VoiceFoundationProof";
import PrivateVideoFoundationProof from "@/pages/PrivateVideoFoundationProof";
import BookEntry from "@/pages/book/BookEntry";
import BookContent from "@/pages/book/BookContent";
import AlmondBookContents from "@/pages/book/AlmondBookContents";
import { isAlmondTomeBookRoute } from "@/lib/almondTomeBookRouteV1.mjs";
import BookReader from "@/pages/book/BookReader";
import RecipeOnlyReader from "@/pages/book/RecipeOnlyReader";
import ProductWorkspace from "@/pages/admin/ProductWorkspace";
import OpenLibraryWorkspace from "@/pages/admin/OpenLibraryWorkspace";
import { EditorialHome, EditorialMaterialEditor } from "@/pages/admin/Editorial";
import EditorialV2 from "@/pages/admin/EditorialV2";
import EditorialMigrationAudit from "@/pages/admin/EditorialMigrationAudit";
import { Toaster } from "@/components/ui/sonner";
import PublicLegalFooter from "@/components/PublicLegalFooter";
import PwaConnectivityGate from "@/pwa/PwaConnectivityGate";

function AlmondBookEntryRoute() {
  const { slug } = useParams();
  const { search } = useLocation();
  return isAlmondTomeBookRoute(decodeURIComponent(slug || ""))
    ? <Navigate to={`/book/${encodeURIComponent(slug)}${search}`} replace />
    : <BookEntry />;
}

function AlmondBookContentsRoute() {
  const { slug } = useParams();
  return isAlmondTomeBookRoute(decodeURIComponent(slug || ""))
    ? <AlmondBookContents />
    : <BookContent />;
}

function App() {
  return (
    <div className="App">
      <PwaConnectivityGate>
        <BrowserRouter>
          <AuthProvider>
            <LocalizationProvider>
            <HallReturnRouteMemory />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Client public Order page — NO auth, NO MOShell, NO redirect to /login */}
            <Route path="/order/:token" element={<PublicOrderView />} />

            {/* Public legal center — available without authentication */}
            <Route path="/legal" element={<LegalCenter />} />
            <Route path="/legal/data-processing" element={<LegalDataProcessing />} />
            <Route path="/legal/publication-consent" element={<LegalPublicationConsent />} />
            <Route path="/legal/offer" element={<LegalOffer />} />
            <Route path="/legal/marketing-consent" element={<LegalMarketingConsent />} />
            <Route path="/legal/ru-personal-data-consent" element={<LegalRuPersonalDataConsent />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/checkout/:offerKey" element={<CheckoutStart />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />

            <Route path="/" element={<WelcomeDoor />} />
            <Route path="/hall" element={<MainHall />} />
            <Route path="/professional-map" element={<ProfessionalMapLanding />} />
            <Route path="/professional-map/profiles/:cardId" element={<ProfessionalMapPublicProfile />} />
            <Route path="/professional-map/reviews/staff" element={<ProtectedRoute><ProfessionalMapReviewsStaff /></ProtectedRoute>} />
            <Route path="/professional-map/discover" element={<ProfessionalMapDiscovery />} />
            <Route path="/professional-map/questions" element={<ProfessionalQuestionsPage />} />
            <Route path="/professional-map/questions/staff" element={<ProtectedRoute><ProfessionalQuestionsStaff /></ProtectedRoute>} />
            <Route path="/professional-map/questions/:questionId" element={<ProfessionalQuestionDetail />} />
            <Route path="/professional-map/:sectionSlug" element={<ProfessionalMapSection />} />
            <Route path="/account/professional-cards" element={<ProtectedRoute><ProfessionalCardWorkspace /></ProtectedRoute>} />
            <Route path="/account/professional-cards/search-qa" element={<ProtectedRoute><ProfessionalMapOwnerQa /></ProtectedRoute>} />
            <Route path="/architect" element={<ProtectedRoute><ArchitectGate /></ProtectedRoute>} />
            <Route path="/architect/:id" element={<ProtectedRoute><ArchitectGate /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><MyConstructions /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/account/materials/:id" element={<ProtectedRoute><MyMaterialDetail /></ProtectedRoute>} />

            <Route path="/library" element={<Library />} />
            <Route path="/library/editorial/:slug" element={<EditorialPublicMaterial />} />
            <Route path="/library/:slug" element={<MaterialDetail />} />

            <Route path="/products/:slug" element={<ProtectedRoute><ProductView /></ProtectedRoute>} />
            <Route path="/products/:pslug/materials/:mslug" element={<ProtectedRoute><ProtectedMaterialDetail /></ProtectedRoute>} />

            <Route path="/master-order-testing" element={<MasterOrderTesting />} />
            <Route path="/master-order-testing/feedback" element={<MasterOrderTestingFeedback />} />

            <Route path="/master-order" element={<ProtectedRoute>
              <MOAccessProvider><MasterOrderOverview /></MOAccessProvider>
            </ProtectedRoute>} />
            <Route path="/master-order/orders" element={<ProtectedRoute><MOAccessProvider><MOOrders /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/orders/new" element={<ProtectedRoute><MOAccessProvider><MOOrderNew /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/orders/:orderId" element={<ProtectedRoute><MOAccessProvider><MOOrderDetail /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/clients" element={<ProtectedRoute><MOAccessProvider><MOClients /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/clients/:clientId" element={<ProtectedRoute><MOAccessProvider><MOClientDetail /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/costings" element={<ProtectedRoute><MOAccessProvider><MOCostings /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/costings/new" element={<ProtectedRoute><MOAccessProvider><MOCostingNew /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/costings/:costingId" element={<ProtectedRoute><MOAccessProvider><MOCostingStudio /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/ingredients" element={<ProtectedRoute><MOAccessProvider><MOIngredients /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/recipes" element={<ProtectedRoute><MOAccessProvider><MORecipes /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/recipe-recalc" element={<ProtectedRoute><MOAccessProvider><MORecipeRecalc /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/measure-directory" element={<ProtectedRoute><MOAccessProvider><MOMeasureDirectory /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/calendar" element={<ProtectedRoute><MOAccessProvider><MOCalendar /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/product-cost" element={<ProtectedRoute><MOAccessProvider><MOProductCost /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio" element={<ProtectedRoute><BrandStudioAccessProvider><MOBrandStudioHomeV2 /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/create" element={<ProtectedRoute><BrandStudioAccessProvider><BrandStudioCreateV2 /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/my-brand" element={<ProtectedRoute><BrandStudioAccessProvider><BrandStudioSpaceV2 space="my-brand" /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/workshop" element={<ProtectedRoute><BrandStudioAccessProvider><BrandStudioSpaceV2 space="workshop" /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/possibilities" element={<ProtectedRoute><BrandStudioAccessProvider><BrandStudioSpaceV2 space="possibilities" /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/brand-portrait" element={<BrandStudioLegacyRedirect to="/brand-studio/create" />} />
            <Route path="/brand-studio/brand-brief" element={<BrandStudioLegacyRedirect to="/brand-studio/create" />} />
            <Route path="/brand-studio/references" element={<ProtectedRoute><BrandStudioAccessProvider><MOBrandReferences /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/visual-direction/new" element={<ProtectedRoute><BrandStudioAccessProvider><MONewVisualDirection /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/visual-direction" element={<ProtectedRoute><BrandStudioAccessProvider><MOVisualDirection /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/visual-dna" element={<ProtectedRoute><BrandStudioAccessProvider><MOVisualDNA /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/visual-dna/:moduleType" element={<ProtectedRoute><BrandStudioAccessProvider><MOVisualDNA /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/logos" element={<ProtectedRoute><BrandStudioAccessProvider><MOLogoWorkshop /></BrandStudioAccessProvider></ProtectedRoute>} />
            <Route path="/brand-studio/:workspaceSlug" element={<ProtectedRoute><BrandStudioAccessProvider><MOBrandStudioWorkspace /></BrandStudioAccessProvider></ProtectedRoute>} />

            <Route path="/master-order/brand-studio" element={<BrandStudioLegacyRedirect to="/brand-studio" />} />
            <Route path="/master-order/brand-studio/create" element={<BrandStudioLegacyRedirect to="/brand-studio/create" />} />
            <Route path="/master-order/brand-studio/references" element={<BrandStudioLegacyRedirect to="/brand-studio/references" />} />
            <Route path="/master-order/brand-studio/visual-direction" element={<BrandStudioLegacyRedirect to="/brand-studio/visual-direction" />} />
            <Route path="/master-order/brand-studio/logos" element={<BrandStudioLegacyRedirect to="/brand-studio/logos" />} />
            <Route path="/master-order/brand-studio/:workspaceSlug" element={<BrandStudioLegacyWorkspaceRedirect />} />
            <Route path="/master-order/packaging-items" element={<ProtectedRoute><MOAccessProvider><MOPackaging /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/purchases" element={<ProtectedRoute><MOAccessProvider><MOPurchases /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/production" element={<ProtectedRoute><MOAccessProvider><MOProduction /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/master-order/production/scenarios" element={<ProtectedRoute><MOAccessProvider><MOProductionScenarios /></MOAccessProvider></ProtectedRoute>} />
            <Route path="/media-studio" element={<ProtectedRoute>
              <ProductAccessGate productSlug="media-studio" productTitle="Медиа-студия">
                <Placeholder title="Медиа-студия" subtitle="Для соцсетей"
                  description="Медиа-студия для создания контента и материалов для соцсетей." />
              </ProductAccessGate>
            </ProtectedRoute>} />
            <Route path="/programs" element={<ProgramsLanding />} />
            <Route path="/programs/almond" element={<AlmondLanding />} />
            <Route path="/programs/:slug" element={<ProgramDetail />} />
            <Route path="/gingerbread" element={<ProtectedRoute>
              <ProductAccessGate productSlug="gingerbread-workshop" productTitle="Пряничная мастерская">
                <GingerbreadLanding />
              </ProductAccessGate>
            </ProtectedRoute>} />
            <Route path="/owner/voice-foundation" element={<OwnerRoute><VoiceFoundationProof /></OwnerRoute>} />
            <Route path="/owner/private-video-foundation" element={<OwnerRoute><PrivateVideoFoundationProof /></OwnerRoute>} />

            {/* Book — «Лаборатория превращений» */}
            <Route path="/book/:slug/entry" element={<AlmondBookEntryRoute />} />
            <Route path="/book/:slug" element={<ProtectedRoute><AlmondBookContentsRoute /></ProtectedRoute>} />
            <Route path="/book/:slug/pages/:pageId" element={<ProtectedRoute><BookReader /></ProtectedRoute>} />
            <Route path="/book/:slug/pages/:pageId/recipes/:blockId" element={<ProtectedRoute><RecipeOnlyReader /></ProtectedRoute>} />

            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/admin/products/:productId" element={<AdminRoute><ProductWorkspace /></AdminRoute>} />
            <Route path="/admin/open-library" element={<AdminRoute><OpenLibraryWorkspace /></AdminRoute>} />
            <Route path="/admin/editorial" element={<EditorialRoute><EditorialHome /></EditorialRoute>} />
            <Route path="/admin/editorial/materials/:materialId" element={<EditorialRoute><EditorialMaterialEditor /></EditorialRoute>} />
            <Route path="/admin/editorial-v2/materials/:materialId/preview-in-library" element={<EditorialRoute><Library draftPreview /></EditorialRoute>} />
            <Route path="/admin/editorial-v2/materials/:materialId" element={<EditorialRoute><EditorialV2 /></EditorialRoute>} />
            <Route path="/admin/editorial/migration-audit" element={<OwnerRoute><EditorialMigrationAudit /></OwnerRoute>} />
            <Route path="/admin/preview/materials/:id" element={<AdminRoute><AdminMaterialPreview /></AdminRoute>} />
            </Routes>
              <PublicLegalFooter />
              <Toaster position="top-center" />
            </LocalizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </PwaConnectivityGate>
    </div>
  );
}

export default App;
